(() => {
  if (window.intagCollectionBrowser) return;
  window.intagCollectionBrowser = true;
  const sessions = new Map();

  async function parallel(items, worker) {
    const result = new Array(items.length);
    let next = 0;
    await Promise.all(Array.from({ length: Math.min(3, items.length) }, async () => {
      while (next < items.length) {
        const index = next++;
        result[index] = await worker(items[index]);
      }
    }));
    return result;
  }

  function slug(value) {
    return String(value).normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function isLaptop(item) {
    const type = String(item.type || '').toLowerCase();
    const title = String(item.title || '').toLowerCase();
    if (/^(dji|poly)\b/.test(title)) return false;
    if (/\blaptops?\b|\bchromebook\b/.test(type)) return true;
    if (type) return false;
    if (/\b(bag|backpack|headset|headphone|earbud|keyboard|mouse|printer|cooling pad|camera|gimbal|charger|adapter|dock)\b/.test(title)) return false;
    return /\b(laptop|notebook|chromebook|thinkpad|elitebook|probook|zbook|latitude|macbook|inspiron|pavilion|victus|ideapad|legion|precision|omen|dragonfly|zenbook|envy|spectre)\b/.test(title);
  }

  function hpAccessory(item) {
    const type = String(item.type || '').toLowerCase();
    return type.includes('headset') || type.includes('printer') ||
      item.handle.includes('poly-') || String(item.title).toLowerCase().includes('printer');
  }

  function text(template, values) {
    return template.replace(/__(\w+)__/g, (_, key) => String(values[key.toLowerCase()] ?? ''));
  }

  async function initialize(section) {
    const node = section.querySelector('[data-intag-catalog-state]');
    const old = sessions.get(section);
    if (!node || old?.node === node) return;
    old?.controller.abort();
    const controller = new AbortController();
    const session = { node, controller };
    sessions.set(section, session);
    let state;
    try { state = JSON.parse(node.textContent); } catch { return; }
    const grid = section.querySelector('.product-list--collection');
    const pager = section.querySelector('[data-intag-stock-pager]');
    if (!grid || !pager) return;
    const pageSize = Number(state.pageSize) || 48;
    const current = new URL(location.href);
    const selectedTag = current.searchParams.get('intag_tag') || '';
    const sort = current.searchParams.get('sort_by') || 'price-ascending';
    const collectionRoot = new URL(state.collectionsUrl + '/', location.origin);
    const base = state.used ? new URL('all', collectionRoot) : new URL(current);
    if (state.used && current.searchParams.has('preview_theme_id')) {
      base.searchParams.set('preview_theme_id', current.searchParams.get('preview_theme_id'));
    }
    ['page', 'intag_page', 'intag_tag', 'section_id', 'sections'].forEach(key => base.searchParams.delete(key));
    const cache = new Map();
    const stillCurrent = () => section.isConnected && section.querySelector('[data-intag-catalog-state]') === node && !controller.signal.aborted;

    function endpoint(source, sectionId, page) {
      const url = new URL(source);
      url.searchParams.set('section_id', sectionId);
      if (page > 1) url.searchParams.set('page', String(page));
      else url.searchParams.delete('page');
      return url;
    }

    function request(url) {
      const key = url.href;
      if (!cache.has(key)) {
        cache.set(key, (async () => {
          const timeout = new AbortController();
          const abort = () => timeout.abort();
          controller.signal.addEventListener('abort', abort, { once: true });
          const timer = setTimeout(abort, 15000);
          try {
            const response = await fetch(url, { credentials: 'same-origin', signal: timeout.signal });
            if (!response.ok) throw new Error('Catalog unavailable');
            return new DOMParser().parseFromString(await response.text(), 'text/html');
          } finally {
            clearTimeout(timer);
            controller.signal.removeEventListener('abort', abort);
          }
        })());
      }
      return cache.get(key);
    }

    async function indexPage(source, page) {
      const doc = await request(endpoint(source, 'intag-catalog-index', page));
      const index = doc.querySelector('[data-intag-catalog-index]');
      if (!index) throw new Error('Catalog index missing');
      return { items: JSON.parse(index.textContent), pages: Number(index.dataset.pages) || 1 };
    }

    async function loadIndex(source) {
      const first = await indexPage(source, 1);
      const extra = await parallel(Array.from({ length: first.pages - 1 }, (_, i) => i + 2), page => indexPage(source, page));
      return [first, ...extra].flatMap(page => page.items);
    }

    function handleOf(card) {
      const anchor = card.querySelector('a[href*="/products/"]');
      if (!anchor) return '';
      return decodeURIComponent(new URL(anchor.href, location.origin).pathname.split('/products/')[1]?.split('/')[0] || '');
    }

    function pageUrl(page) {
      const url = new URL(current);
      ['intag_page', 'section_id', 'sections'].forEach(key => url.searchParams.delete(key));
      if (page > 1) url.searchParams.set('page', String(page));
      else url.searchParams.delete('page');
      return url.pathname + url.search;
    }

    function pageLink(page, label, className) {
      const link = document.createElement('a');
      link.href = pageUrl(page);
      link.className = className;
      link.dataset.page = String(page);
      link.textContent = label;
      return link;
    }

    function pagination(page, total) {
      const inner = pager.querySelector('.pagination__inner');
      if (!inner) return;
      const parts = [];
      if (page > 1) parts.push(pageLink(page - 1, state.labels.previous, 'pagination__prev link'));
      const nav = document.createElement('div');
      nav.className = 'pagination__nav';
      let previous = 0;
      for (let n = 1; n <= total; n++) {
        if (total > 9 && n !== 1 && n !== total && Math.abs(n - page) > 2) continue;
        if (n - previous > 1) {
          const gap = document.createElement('span');
          gap.className = 'pagination__nav-item';
          gap.textContent = '…';
          nav.appendChild(gap);
        }
        if (n === page) {
          const active = document.createElement('span');
          active.className = 'pagination__nav-item is-active';
          active.setAttribute('aria-current', 'page');
          active.textContent = String(n);
          nav.appendChild(active);
        } else nav.appendChild(pageLink(n, String(n), 'pagination__nav-item link'));
        previous = n;
      }
      parts.push(nav);
      const count = document.createElement('span');
      count.className = 'pagination__page-count';
      count.textContent = text(state.labels.page, { current: page, total });
      parts.push(count);
      if (page < total) parts.push(pageLink(page + 1, state.labels.next, 'pagination__next link'));
      inner.replaceChildren(...parts);
      pager.hidden = total <= 1;
    }

    function renderFilters(items) {
      if (!state.used) return;
      const tags = new Map();
      for (const item of items) {
        for (const tag of item.tags || []) {
          if (tag && !tag.includes('__')) tags.set(slug(tag), tag);
        }
      }
      for (const panel of section.querySelectorAll('[data-intag-used-filters]')) {
        const list = document.createElement('ul');
        list.className = 'collection__filter-checkbox-list';
        [...tags].sort((a, b) => a[1].localeCompare(b[1])).forEach(([value, name], i) => {
          const row = document.createElement('li');
          row.className = 'collection__filter-checkbox';
          const wrap = document.createElement('div');
          wrap.className = 'checkbox-wrapper';
          const box = document.createElement('input');
          box.type = 'checkbox';
          box.className = 'checkbox';
          box.id = 'intag-used-' + panel.dataset.intagUsedFilters + '-' + i;
          box.checked = value === selectedTag;
          box.addEventListener('change', () => {
            const url = new URL(current);
            ['page', 'intag_page', 'filter.v.availability'].forEach(key => url.searchParams.delete(key));
            if (box.checked) url.searchParams.set('intag_tag', value);
            else url.searchParams.delete('intag_tag');
            location.assign(url.pathname + url.search);
          }, { signal: controller.signal });
          const label = document.createElement('label');
          label.htmlFor = box.id;
          label.textContent = name;
          wrap.appendChild(box);
          row.append(wrap, label);
          list.appendChild(row);
        });
        const parts = [];
        if (selectedTag) {
          const clear = document.createElement('a');
          const url = new URL(current);
          ['intag_tag', 'page', 'intag_page'].forEach(key => url.searchParams.delete(key));
          clear.href = url.pathname + url.search;
          clear.className = 'intag-used-clear-filter link';
          clear.textContent = state.labels.clear;
          parts.push(clear);
        }
        parts.push(list);
        panel.replaceChildren(...parts);
      }
    }

    try {
      const brandUrl = new URL('brand-new-laptops-pakistan', collectionRoot);
      if (base.searchParams.has('preview_theme_id')) brandUrl.searchParams.set('preview_theme_id', base.searchParams.get('preview_theme_id'));
      const [raw, brand] = await Promise.all([loadIndex(base), state.used ? loadIndex(brandUrl) : Promise.resolve([])]);
      const brandHandles = new Set(brand.map(item => item.handle));
      const seen = new Set();
      let items = raw.map((item, rawPosition) => ({ ...item, rawPosition })).filter(item => {
        if (seen.has(item.handle)) return false;
        seen.add(item.handle);
        if (state.hpOnly && hpAccessory(item)) return false;
        return !state.used || (!brandHandles.has(item.handle) && isLaptop(item));
      });
      const allChoices = items;
      if (state.used) {
        if (selectedTag) items = items.filter(item => (item.tags || []).some(tag => slug(tag) === selectedTag));
        if (state.tags?.length) items = items.filter(item => state.tags.every(tag => (item.tags || []).includes(tag)));
        const availability = current.searchParams.get('filter.v.availability');
        if (availability === '1') items = items.filter(item => item.available);
        if (availability === '0') items = items.filter(item => !item.available);
      }
      items.sort((a, b) => {
        if (a.available !== b.available) return a.available ? -1 : 1;
        if (sort === 'price-ascending') return Number(a.price) - Number(b.price) || a.rawPosition - b.rawPosition;
        if (sort === 'price-descending') return Number(b.price) - Number(a.price) || a.rawPosition - b.rawPosition;
        return a.rawPosition - b.rawPosition;
      });
      const total = Math.max(1, Math.ceil(items.length / pageSize));
      const requested = Number(current.searchParams.get('intag_page')) || Number(state.page) || 1;
      if (requested > total) return;
      const page = Math.max(1, requested);
      const displayed = items.slice((page - 1) * pageSize, page * pageSize);
      const needed = [...new Set(displayed.map(item => Math.floor(item.rawPosition / pageSize) + 1))];
      const cardsByPage = new Map(await parallel(needed, async sourcePage => {
        const doc = await request(endpoint(base, state.sectionId, sourcePage));
        const cards = new Map();
        for (const card of doc.querySelectorAll('.product-list--collection .product-item')) {
          const handle = handleOf(card);
          if (handle) cards.set(handle, card);
        }
        return [sourcePage, cards];
      }));
      const cards = displayed.map(item => cardsByPage.get(Math.floor(item.rawPosition / pageSize) + 1)?.get(item.handle));
      if (cards.some(card => !card)) throw new Error('Product cards missing');
      if (!stillCurrent()) return;
      grid.replaceChildren(...cards.map(card => card.cloneNode(true)));
      pagination(page, total);
      const showing = section.querySelector('.collection__showing-count');
      if (showing) showing.textContent = text(state.labels.showing, { first: items.length ? (page - 1) * pageSize + 1 : 0, last: Math.min(page * pageSize, items.length), total: items.length });
      renderFilters(allChoices);
      if (window.lazySizes?.loader) window.lazySizes.loader.checkElems();
      if (window.SPR) { window.SPR.initDomEls(); window.SPR.loadBadges(); }
    } catch (error) {
      if (!controller.signal.aborted) console.warn('Collection enhancement unavailable; native collection preserved.', error);
    }
  }

  function start() {
    if (!active) return;
    for (const [section, session] of sessions) {
      if (!section.isConnected) { session.controller.abort(); sessions.delete(section); }
    }
    document.querySelectorAll('[data-section-type="collection"]').forEach(initialize);
  }
  let frame = null;
  let active = true;
  const observer = new MutationObserver(() => {
    if (!active || frame !== null) return;
    frame = requestAnimationFrame(() => { frame = null; start(); });
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', start);
  document.addEventListener('theme:loading:end', start);
  addEventListener('popstate', start);
  addEventListener('pagehide', () => {
    active = false;
    observer.disconnect();
    if (frame !== null) cancelAnimationFrame(frame);
    frame = null;
    for (const session of sessions.values()) session.controller.abort();
    sessions.clear();
  });
  addEventListener('pageshow', () => {
    if (active) return;
    active = true;
    observer.observe(document.documentElement, { childList: true, subtree: true });
    start();
  });
  start();
})();
